import React, { useRef, useState } from "react";
import api, { extractError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Multipart file uploader. Accepts optional defect_id / claim_id / asset_id / certificate_id
 * that will be sent as query params so the backend attaches the doc.
 */
export default function FileUploader({ defectId, claimId, assetId, certificateId, onUploaded, accept = "image/*,application/pdf", label = "Ajouter photo / document" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const params = new URLSearchParams();
        // Only one attachment target: defect > claim > asset > certificate (backend refuses multiple).
        if (defectId) params.set("defect_id", defectId);
        else if (claimId) params.set("claim_id", claimId);
        else if (assetId) params.set("asset_id", assetId);
        else if (certificateId) params.set("certificate_id", certificateId);
        const { data } = await api.post(`/upload?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded?.(data);
      }
      toast.success(`${files.length} fichier(s) envoyé(s)`);
    } catch (e) {
      const msg = typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Échec de l'upload";
      toast.error(msg);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="file-drop-zone"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer?.files || [])); }}
      className={`rounded-md border-2 border-dashed transition ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-200"} p-3 text-center`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        data-testid="file-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="upload-btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full"
      >
        {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
        {busy ? "Envoi..." : label}
      </Button>
      <div className="text-[10px] text-slate-400 mt-1.5">Glissez-déposez photos (jpg, png) ou PDF ici</div>
    </div>
  );
}
